// Two-pane navigation:
//   • Primary rail (icon + label) on the far left — collapsible to icon-only.
//   • When a primary item with children is active, a secondary pane shows the
//     submenu items.
//   • Single-page items navigate directly with no submenu pane.
//   • Bottom of the rail: avatar chip (logged-in user), theme toggle, sign-out.

import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Home, Users, Phone, ArrowUpRight, BarChart3, Settings, Building2,
  Globe, Server, Sliders, Briefcase, ClipboardList, Shield, KeyRound, MessageSquare, Radio, Router, Network,
  PackageOpen, Mic2, Calendar, LogOut, LayoutGrid, Volume2, Voicemail as VoicemailIcon,
  ChevronLeft, ChevronRight, Sun, Moon, User as UserIcon,
  BellRing, BedDouble, FolderSync, PhoneCall,
} from 'lucide-react';
import { api, setToken } from '../lib/api';
import { BrandLogo } from './BrandLogo';
import { useTheme } from '../lib/theme';
import { getActiveTenant, setActiveTenant, ALL } from '../lib/tenantScope';
import Copilot from './Copilot';

type Sub = { to: string; label: string };
type Top = { to: string; icon: any; label: string; children?: Sub[]; resellerOnly?: boolean; superOnly?: boolean };

const NAV: Top[] = [
  { to: '/', icon: Home, label: 'Dashboard' },

  // Single flat page — no sub-menu, matches 3CX shape.
  { to: '/users', icon: Users, label: 'Users' },

  // In-browser WebRTC softphone (v1): register your own extension over WSS,
  // dial/answer with mic audio. Visible to everyone — it only ever exposes
  // the logged-in user's OWN line (/api/webphone/config).
  { to: '/webphone', icon: PhoneCall, label: 'Web Phone' },

  // Voice & Chat — single entry; Trunks / SBCs / Messaging / Fax-DECT / Gateways / Bridges are tabs inside the page.
  // SIP trunk editing (incl. per-trunk SMS) is system-admin only.
  { to: '/voice-chat', icon: Globe, label: 'Voice & Chat', superOnly: true },

  // Single flat page. Sub-pages (PNP / Hot Desking / Firmwares) reached via
  // the toolbar on /phones. Templates + auto-provisioning live under Advanced.
  { to: '/phones', icon: Phone, label: 'Phones' },

  // System-wide dialplan — a bad rule can hijack/break outbound for every department. System-admin only.
  { to: '/outbound-rules-v2', icon: ArrowUpRight, label: 'Outbound Rules', superOnly: true },

  // Single flat page. E911 locations live inside the Edit Department modal
  // as a sub-tab; legacy Emergency Geo is reachable via direct URL only.
  { to: '/departments', icon: Briefcase, label: 'Departments' },

  // Holidays moved to a tab inside the Office Hours page (matches 3CX shape).
  { to: '/office-hours', icon: Calendar, label: 'Office Hours' },

  // Inbound Rules removed from top-level — 3CX folds inbound routing into
  // the trunk row's "Route to" cell + per-DID destinations (Voice & Chat).
  // Time-of-day variations live on the destination (Ring Group / Queue / IVR
  // Office Hours), not on a separate rule list. Caller-ID pattern routing
  // remains at Advanced → CID Rules. /inbound-rules still works as a URL for
  // power users who want the full list view.
  // Flat page — unified list of Ring Groups + Queues + Digital Receptionists
  // (matches 3CX shape). Stratus-specific extras (Call Flow Designer, Skill
  // Routing, AI Agents) moved under Advanced; Boss-Secretary lives in the user
  // editor's Call Forwarding tab.
  { to: '/ring-groups', icon: Phone, label: 'Call Handling' },

  // Shared / Group Voicemail — department mailboxes decoupled from a single user
  // (front desk, billing…), several extensions share one message store + MWI.
  { to: '/shared-voicemail', icon: VoicemailIcon, label: 'Shared Voicemail' },

  // Boss-Secretary: priority call routing rules (secretary screens calls for boss).
  // Route exists in App.tsx; previously unreachable from the sidebar.
  { to: '/boss-secretary', icon: Shield, label: 'Boss-Secretary', superOnly: true },

  // Paging Groups — dedicated view of ring groups using the paging/multicast strategy.
  { to: '/paging-groups', icon: Volume2, label: 'Paging Groups' },

  // Flat — Call History + Wallboard are reachable from inside the Reports landing.
  { to: '/reports', icon: BarChart3, label: 'Reports' },

  // Flat — single Event Log unifies audit_log_v2 + activity_log into the 3CX shape.
  { to: '/event-log', icon: ClipboardList, label: 'Event Log' },

  // Flat — single Recordings page (matches 3CX). Voicemail isn't a sidebar
  // entry in 3CX — it's reached via the badged icon in the icon column at
  // the far left. /voicemail still routes for direct URL access.
  { to: '/recordings', icon: Mic2, label: 'Recordings' },

  // Backup/restore covers the WHOLE database (all departments). System-admin only.
  { to: '/backup', icon: PackageOpen, label: 'Backup', superOnly: true },

  // Flat — single Integrations entry. The page itself owns an internal rail
  // (CRM / AI / Call Scripts / Google / Amazon / M365 / Teams / Hotel Services
  // / Transcription / API / Data Connectors) matching 3CX's secondary nav.
  // Contacts / Public Talk URLs / Meet are still routable directly but kept
  // out of the sidebar to mirror 3CX's flat top-level shape.
  { to: '/integrations', icon: Server, label: 'Integrations', superOnly: true },

  // Per-deployment configuration (notifications, credentials) — the resale/customization layer.
  { to: '/settings', icon: KeyRound, label: 'Settings', superOnly: true },

  // Hotel Lite: wake-up call scheduler and room status board.
  // Routes exist in App.tsx; previously unreachable from the sidebar.
  { to: '/wakeup-calls', icon: BellRing, label: 'Wake-up Calls', superOnly: true },
  { to: '/room-status',  icon: BedDouble, label: 'Room Status',   superOnly: true },

  // LDAP/AD contact directory sync — company phonebook from Active Directory (3CX parity).
  { to: '/ldap-sync', icon: FolderSync, label: 'Directory Sync', superOnly: true },

  // Flat — single Advanced entry. The page owns its own rail (IP Blacklist /
  // Anti-Hacking / Console Restrictions / Network / Country Codes / CID Rules
  // / CID Blacklist / Secure SIP / Codecs / Parameters / Templates / Fax /
  // E164 / CDR) matching 3CX's secondary nav. Stratus extras (Call Flow
  // Designer, Skill Routing, AI Agents) remain reachable via direct URL but
  // stay out of the rail.
  { to: '/advanced', icon: Sliders, label: 'Advanced', superOnly: true },

  { to: '/system/license', icon: Settings, label: 'System', superOnly: true, children: [
    { to: '/system/license',      label: 'License' },
    { to: '/system/billing',      label: 'Billing' },
    { to: '/system/updates',      label: 'Updates' },
    { to: '/groups',              label: 'Groups & Roles' },
    { to: '/alerts',              label: 'Alerts' },
    { to: '/system/maintenance',  label: 'Maintenance' },
    { to: '/system/options',      label: 'Options' },
    { to: '/system/theme',        label: 'Theme & Style' },
    { to: '/system/dial-codes',   label: 'Dial Codes' },
    { to: '/system/parking',      label: 'Call Parking' },
    { to: '/pickup-groups',       label: 'Pickup Groups' },
    { to: '/system/emergency',    label: 'Emergency' },
    { to: '/system/phonebook',    label: 'Phonebook' },
    { to: '/system/conferencing', label: 'Conferencing' },
    { to: '/conference-rooms',   label: 'Conference Rooms' },
    { to: '/system/email',        label: 'Email' },
    { to: '/system/moh',          label: 'Music on Hold' },
    { to: '/system/system-prompts', label: 'System Prompts' },
    { to: '/system/voicemail',    label: 'Voicemail' },
    { to: '/system/storage',      label: 'Storage' },
    { to: '/system/remote-voicemail', label: 'Remote Voicemail' },
    { to: '/system/multi-company', label: 'Multi Company' },
    { to: '/system/firewall',     label: 'Firewall' },
    { to: '/system/services',     label: 'Services' },
    { to: '/health',              label: 'System Health' },
    { to: '/security',            label: 'Security' },
    { to: '/ip-allowlist',        label: 'IP Allowlist' },
  ]},

  // Reseller tab hidden from the menu for now (route still works at /reseller).
  // { to: '/reseller', icon: Building2, label: 'Reseller', resellerOnly: true },

  // Download the Stratus apps (iOS / Android mobile + Windows desktop) — pinned to the bottom.
  { to: '/apps', icon: LayoutGrid, label: 'Apps' },
];

function findActiveTop(pathname: string): Top | undefined {
  for (const top of NAV) {
    if (top.children?.some(c => pathname === c.to || pathname.startsWith(c.to + '/'))) return top;
  }
  for (const top of NAV) {
    if (pathname === top.to || (top.to !== '/' && pathname.startsWith(top.to + '/'))) return top;
  }
  return NAV[0];
}

const COLLAPSE_KEY = 'stratus-sidebar-collapsed';

export default function Layout() {
  const me = api.me();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  const isReseller = me?.role === 'super_admin' || me?.role === 'reseller';
  // System-wide nav (Voice&Chat, Outbound Rules, Backup, Integrations, Settings, Advanced,
  // System) is visible to super_admin / reseller AND an app_role System Admin/Owner — but NOT
  // a Department Administrator, who is pinned to their own department. (#451)
  const isSysAdmin = isReseller || (!!me?.app_role && /^system\b/i.test(me.app_role));
  const visibleNav = useMemo(
    () => NAV.filter(n => (!n.resellerOnly || isReseller) && (!n.superOnly || isSysAdmin)),
    [isReseller, isSysAdmin]
  );
  const activeTop = useMemo(() => findActiveTop(location.pathname), [location.pathname]);

  // Department view-scope: a super_admin / reseller can filter the whole console
  // to one department. The selection is read by lib/api.ts and injected as
  // ?domain_uuid on GETs; we reload on change so every page (react-query AND
  // useEffect-based fetchers) re-fetches scoped. View only — not enforcement.
  const canScope = me?.role === 'super_admin' || me?.role === 'reseller';
  const { data: deptList } = useQuery({
    queryKey: ['dept-scope-list'],
    queryFn: () => api.tenants.list(),
    enabled: canScope,
  });
  const activeTenant = getActiveTenant();
  function changeDept(v: string) {
    setActiveTenant(v);
    window.location.reload();
  }

  const { data: alertCounts } = useQuery({
    queryKey: ['alert-counts', me?.domain_uuid],
    queryFn: () => api.adminNotifications.counts(me?.domain_uuid),
    refetchInterval: 30_000,
    enabled: !!me,
  });
  const unread = alertCounts?.total || 0;
  const hasCritical = (alertCounts?.critical || 0) > 0 || (alertCounts?.error || 0) > 0;

  function logout() {
    api.logout().catch(() => {});
    setToken(null);
    navigate('/login');
  }

  const railWidth = collapsed ? 'w-14' : 'w-44';

  return (
    <div className="flex h-screen bg-bg">
      {/* Primary rail */}
      <aside className={`${railWidth} border-r border-border bg-surface flex-shrink-0 flex flex-col transition-[width] duration-200`}>
        {/* Logo + collapse toggle */}
        <div className="p-3 border-b border-border flex items-center justify-between">
          {!collapsed ? (
            <Link to="/" className="flex items-center gap-2 min-w-0"><BrandLogo /></Link>
          ) : (
            <Link to="/" className="mx-auto" title="Stratus"><BrandLogo collapsed /></Link>
          )}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              className="text-textSecondary hover:text-textPrimary p-1 rounded hover:bg-elevated"
            ><ChevronLeft size={16} /></button>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-auto p-2 space-y-0.5">
          {visibleNav.map(n => {
            const isActive = activeTop === n;
            const target = n.children && n.children.length > 0 ? n.children[0].to : n.to;
            const showAlertBadge = (n.children?.some(c => c.to === '/alerts')) || n.to === '/alerts';
            const badge = showAlertBadge && unread > 0
              ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${hasCritical ? 'bg-danger text-white' : 'bg-warning text-black'}`}>{unread > 99 ? '99+' : unread}</span>
              : null;
            return (
              <Link
                key={n.label}
                to={target}
                title={collapsed ? n.label : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? 'bg-primary text-white' : 'text-textPrimary hover:bg-elevated'
                } ${collapsed ? 'justify-center px-2' : ''}`}
              >
                <n.icon size={16} className="shrink-0" />
                {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
                {!collapsed && badge}
                {collapsed && badge && <span className="absolute" />}
              </Link>
            );
          })}
        </nav>

        {/* Bottom: theme toggle + expand button + user chip */}
        <div className="border-t border-border p-2 space-y-1">
          {collapsed && (
            <button
              onClick={() => setCollapsed(false)}
              title="Expand sidebar"
              className="w-full flex justify-center py-2 rounded-md hover:bg-elevated text-textSecondary"
            ><ChevronRight size={16} /></button>
          )}

          {/* Hop to the user webclient (Chat / Calls / Contacts / Voicemail / Settings). */}
          <Link
            to="/webclient"
            title="Open my webclient"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-elevated text-sm ${collapsed ? 'justify-center px-2' : ''}`}
          >
            <LayoutGrid size={16} className="text-primary" />
            {!collapsed && <span className="text-textSecondary">Webclient</span>}
          </Link>

          <button
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-elevated text-sm ${collapsed ? 'justify-center px-2' : ''}`}
          >
            {theme === 'dark' ? <Sun size={16} className="text-warning"/> : <Moon size={16} className="text-primary"/>}
            {!collapsed && <span className="text-textSecondary">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
          </button>

          {/* User chip / sign-out */}
          <div className={`flex items-center gap-2 px-2 py-2 rounded-md hover:bg-elevated ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
              {(me?.username || '?').slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{me?.username || 'unknown'}</div>
                <div className="text-[10px] text-textSecondary truncate">{me?.role || 'user'}</div>
              </div>
            )}
            <button
              onClick={logout}
              title="Sign out"
              className="p-1 rounded hover:bg-danger/10 hover:text-danger text-textSecondary"
            ><LogOut size={14} /></button>
          </div>
        </div>
      </aside>

      {/* Secondary submenu pane (only when active top has children) */}
      {activeTop?.children && activeTop.children.length > 0 && (
        <aside className="w-52 border-r border-border bg-surface flex-shrink-0 overflow-auto">
          <div className="p-4 border-b border-border">
            <div className="text-xs uppercase tracking-wide text-textSecondary">{activeTop.label}</div>
          </div>
          <nav className="p-2 space-y-0.5">
            {activeTop.children.map(c => (
              <NavLink
                key={c.to}
                to={c.to}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-sm ${
                    isActive ? 'bg-primary/15 text-primary font-medium' : 'text-textPrimary hover:bg-elevated'
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </nav>
        </aside>
      )}

      <main className="flex-1 flex flex-col min-h-0">
        {canScope && (
          <div className="flex items-center gap-2 px-6 py-2 border-b border-border bg-surface">
            <Building2 size={15} className="text-textSecondary shrink-0" />
            <span className="text-xs text-textSecondary">Viewing</span>
            <select
              value={activeTenant}
              onChange={e => changeDept(e.target.value)}
              title="Filter the entire console to one department"
              className="bg-elevated border border-border rounded-md px-2 py-1 text-sm text-textPrimary max-w-[18rem]"
            >
              <option value={ALL}>All departments</option>
              {(deptList || []).map((d: any) => (
                <option key={d.domain_uuid} value={d.domain_uuid}>
                  {d.domain_description || d.domain_name || d.domain_uuid}
                </option>
              ))}
            </select>
            {activeTenant !== ALL && (
              <span className="text-[11px] text-primary font-medium">● filtered</span>
            )}
          </div>
        )}
        {/* pb-28 keeps scrolled-to-bottom page actions (Save buttons live bottom-right
            on the edit pages) clear of the fixed Copilot launcher chip. */}
        <div className="flex-1 overflow-auto p-6 pb-28">
          <Outlet />
        </div>
      </main>
      <Copilot />
    </div>
  );
}
